"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _fieldFrame = _interopRequireDefault(require("@ux/field-frame"));
var _button = _interopRequireDefault(require("@ux/button"));
var _textEntry = _interopRequireDefault(require("@ux/text-entry"));
var _flyout = _interopRequireDefault(require("@ux/flyout"));
var _listbox = _interopRequireDefault(require("@ux/listbox"));
var _x = _interopRequireDefault(require("@ux/icon/x"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const UX_SEARCH_CLASSNAME = 'ux-search';
const DEFAULT_ARIA_LABELS = {
  clear: 'Clear search field'
};

/**
 * "Sluggifies" an individual item to ensure uniqueness as an ID
 *
 * @param {String} parentId parent ID to be used in namespacing
 * @param {String} value item value
 * @returns {String} sluggified value
 */
function generateItemId(parentId, value) {
  const valueSlug = String(value).toLowerCase().replace(/[^a-z0-9-]+/g, '');
  return [parentId, 'option', valueSlug].join('--');
}

/**
 * Converts an array of string items into an array of item objects for use in Listbox
 *
 * @param {Array<String>} items Array of string items
 * @param {String} parentId The parent ID to use for namespacing
 * @returns {Array<Object>} array of item objects
 */
function generateItemsArray(items, parentId) {
  return [].concat(items).map(item => ({
    id: generateItemId(parentId, item),
    children: item,
    value: item
  }));
}

/**
 * Creates a search component
 *
 * @param {Object} props - Component properties
 *
 * Search specific params
 * @param {Object} [props.ariaLabels] - Sets aria labels for the internals
 * @param {String} [props.ariaLabels.close] - Label for the clear button
 * @param {Element} [props.button] - Sets a button within the input
 * @param {String} [props.className] - Provides an optional class name to be set on the root
 * @param {Boolean} [props.clearable] - Determines if a clear button should be included
 * @param {Node} [props.children] - Content to show within the flyout
 * @param {Boolean} [props.divider] - Adds an optional divider between the button and input
 * @param {Array<String>} [props.items] - List of possible results to show in flyout
 * @param {Boolean} [props.open] - If content is set within the list, this controls visibility
 * @param {Element} [props.portalElem] - Element for flyout attachment
 * @param {String} [props.strategy] - Flyout positioning
 *
 * @returns {Component} - Search component
 */
const Search = /*#__PURE__*/_react.default.forwardRef(function Search(props, ref) {
  const {
    id,
    ariaLabels,
    button,
    className,
    clearable = true,
    children,
    onConfirm,
    onSelect,
    open = true,
    /* Props above are specific for Search, textEntryProps are for TextEntry */
    role = 'combobox',
    type = 'text',
    placement = 'bottom-start',
    items: _items = [],
    onClose,
    portalElem,
    strategy,
    ...textEntryProps
  } = props || {};
  const {
    invalid,
    disabled,
    onChange,
    onKeyDown,
    value
  } = textEntryProps || {};
  const {
    clear
  } = {
    ...DEFAULT_ARIA_LABELS,
    ...ariaLabels
  };
  const LISTBOX_ID = `${id}-listbox`;
  const items = (0, _react.useMemo)(() => generateItemsArray(_items, id), [_items, id]);
  const textEntryRef = (0, _react.useRef)(null);
  (0, _react.useImperativeHandle)(ref, () => textEntryRef.current);
  const anchorRef = (0, _react.useRef)(null);
  const listboxRef = (0, _react.useRef)(null);
  const hasItems = items.length > 0;
  const show = open && Boolean(hasItems || children);
  const handleOnClear = (0, _react.useCallback)(ev => {
    onChange('', {
      event: ev
    });
    // Move focus back to the text element!
    textEntryRef.current?.focus?.();
  }, [onChange, textEntryRef]);
  const handleTextEntryConfirm = (0, _react.useCallback)(meta => {
    if (typeof onKeyDown === 'function') {
      onKeyDown(meta);
    }
    if (meta.key === 'Enter') {
      onConfirm?.(meta.target.value, meta);
    }
  }, [onConfirm, onKeyDown]);
  const handleListboxConfirm = (0, _react.useCallback)((itemId, meta) => {
    // Need to return what the author gave in the props
    const index = items.findIndex(item => item.id === itemId);
    const item = ~index ? _items[index] : null;
    onChange(item, meta);
    onSelect?.(item, meta);
    onConfirm?.(item, meta);
  }, [onChange, onSelect, onConfirm, _items, items]);
  const content = hasItems ? /*#__PURE__*/_react.default.createElement(_listbox.default, {
    className: `${UX_SEARCH_CLASSNAME}-listbox`,
    ref: listboxRef,
    anchorRef: textEntryRef,
    id: LISTBOX_ID,
    items: items,
    stretch: true,
    loop: true,
    onConfirm: handleListboxConfirm
  }) : children;
  return /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _classnames.default)(UX_SEARCH_CLASSNAME, className, {
      [`${UX_SEARCH_CLASSNAME}--composed`]: button
    })
  }, /*#__PURE__*/_react.default.createElement(_fieldFrame.default, {
    className,
    disabled,
    invalid,
    ref: anchorRef
  }, /*#__PURE__*/_react.default.createElement(_textEntry.default, (0, _extends2.default)({
    autoComplete: "off"
  }, textEntryProps, {
    id: id,
    role: role,
    type: type,
    onKeyDown: handleTextEntryConfirm,
    ref: textEntryRef,
    "aria-autocomplete": hasItems ? 'list' : null,
    "aria-controls": hasItems ? LISTBOX_ID : null /* @TODO provide id for arbitrary component children? */,
    "aria-expanded": show,
    disabled: disabled
  })), show && /*#__PURE__*/_react.default.createElement(_flyout.default, {
    anchorRef: anchorRef,
    placement: placement,
    portalElem: portalElem,
    strategy: strategy,
    stretch: true,
    onClose: onClose
  }, content), clearable && /*#__PURE__*/_react.default.createElement(_button.default, {
    "aria-label": clear,
    "data-cy": "clear-button",
    design: "stateless",
    hidden: !value,
    icon: /*#__PURE__*/_react.default.createElement(_x.default, null),
    onClick: handleOnClear,
    value: ""
  }), button));
});
Search.propTypes = {
  /** Inherited props from FieldFrame */
  ..._fieldFrame.default.propTypes,
  /** Inherited props from TextEntry */
  ..._textEntry.default.propTypes,
  /** Sets aria labels for the internals */
  ariaLabels: _propTypes.default.shape({
    /** Label for the clear button */
    clear: _propTypes.default.string
  }),
  /** Sets a button within the input */
  button: _propTypes.default.element,
  /** Provides an optional class name to be set on the root */
  className: _propTypes.default.string,
  /** Determines if a clear button should be included */
  clearable: _propTypes.default.bool,
  /** Content to show within the flyout */
  children: _propTypes.default.node,
  /** List of possible results to show in flyout */
  items: _propTypes.default.arrayOf(_propTypes.default.string),
  /** Callback when an item is selected within the results flyout or enter key press in the search box. The first argument is the id of the item. */
  onConfirm: _propTypes.default.func,
  /** Callback when an item is selected within the results flyout. The first argument is the id of the item. */
  onSelect: _propTypes.default.func,
  /** If content is set within the list, this controls visibility */
  open: _propTypes.default.bool,
  /** Element for flyout attachment */
  portalElem: _propTypes.default.object,
  /** Flyout positioing */
  strategy: _propTypes.default.oneOf(['absolute', 'fixed'])
};
var _default = exports.default = Search;